import { HttpService } from 'core/modules/network-service';

import { get, head, merge } from 'lodash';
import { SERVICE_RESPONSE_STATUS, CRUD_OPERATION } from 'constants/common';
import { getVisibleItems, isVisible } from 'resolvers/getVisibleEntities';
import { REDUCER } from 'constants/entity-names';
import { getStore } from 'store';

// get store
const store = getStore();

/*************************************************************/

/**
 * @type {class}
 * @desc EpostPaymentInformationStep defines an HTTP service interface to send HTTP network requets.
 * @example
 * import { EpostPaymentInformationStepService } from 'services/epostPaymentInformationStep';
 *
 * // create service instance and send/cancel request
 * const epostPaymentInformationStepService = new EpostPaymentInformationStepService();
 * const result = await epostPaymentInformationStepService.fetchData( '<user-id>' ); // fetch data
 * epostPaymentInformationStepService.cancel(); // cancel an active request
 */
export class EpostPaymentInformationStepService extends HttpService {

    // fetch data from the backend
    async fetchComponentData( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId
                    },
                } )
            } );

             // extract data
             const visibility = get( response, 'body.component.visibility', {} );
             const modifyActions = get( response, 'body.component.structure.modifyActions', {} );
             const addAction = get( response, 'body.component.structure.addAction', {} );
             const component = get( response, 'body.component', {} );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: {
                    options: get( response, 'body.component.data.options', {} ),
                    addAction: isVisible( addAction, visibility ) ? addAction: {},
                    modifyActions: getVisibleItems( modifyActions, visibility ),
                    navActions: get( response, 'body.component.structure.navActions', {} ),
                    widgetConfig: get( response, 'body.component.structure.widgetConfig', {} ),
                    changeConfirmation: get( component, 'structure.widgetConfig.changeConfirmation', {} ),
                    defaultDescription: isVisible( get( component, 'structure.widgetConfig.defaultDescription', {} ), visibility ) ? get( response, 'body.component.structure.widgetConfig.defaultDescription', {} ): {},
                    ...get( response, 'body.component.data.state', {} )
                },
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // make request and fetch data
    async setDefaultPaymentInfo( { url, data } ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId
                    },
                } )
            } );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: get( response, 'body.component.data.state', get( response, 'body.component.data', {} ) ),
            };

        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
            };
        }
    }

    // make request and fetch data
    async deletePaymentInfo( { url, data } ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId
                    },
                } )
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: get( response, 'body.component.data.state', get( response, 'body.component.data', {} ) ),
            };

        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
            };
        }
    }

    // make request and fetch data
    async submitEditedPaymentInfo( { url, data } ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                        operation: CRUD_OPERATION.UPDATE,
                    },
                } ),
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: get( response, 'body.component.data.state', get( response, 'body.component.data', {} ) ),
            };

        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
            };
        }
    }

    // fetch call control widget data
    async submitStepData( { url = {}, data = {} } = {} ) {

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: data
            } );

            if( SERVICE_RESPONSE_STATUS.FAILURE === get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ) ) {

                return {
                    status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                    data: {
                        description: get( response, 'body.component.data.description', {} ),
                    }
                };
            }

            // get correct success message
            const visibility = get( response, 'body.component.visibility' );
            const successMessage = head( getVisibleItems( get( response, 'body.component.structure.widgetInfo.successMessage' ), visibility ) );
            
            // get nav actions
            const navActions = get( response, 'body.component.structure.navActions' );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: {
                    title: get( successMessage, 'title' ),
                    patientId: get( response, 'body.component.data.state.patientId', null ),
                    navActions
                },
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // submits payment card activation request
    async submitActivatePaymentInfoRequest( { url, data } ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: get( response, 'body.component.data.state', get( response, 'body.component.data', {} ) )
            };

        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
            };
        }
    }

    async cancelReactivationFlow( { url = {}, data = {} } = {} ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );
            return get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE );
        } catch( error ) {
            return SERVICE_RESPONSE_STATUS.FAILURE;
        }
    }

    // submits payment card activation request
    async submitToggleHsaFsaPaymentInfoRequest( { url, data } ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: get( response, 'body.component.data.state', get( response, 'body.component.data', {} ) )
            };

        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
            };
        }
    }
}
